/**
 * SystemWorkspacePage
 *
 * Generic page component for system workspaces (Attrition, Recruitment, etc.)
 * Parameterized by workspace ID to render the appropriate workspace content.
 */

import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X } from "lucide-react";
import QueryBar from "@/components/chat/QueryBar";
import {
  addSystemWorkspaceReport,
  getSystemWorkspaceReports,
  removeSystemWorkspaceReport,
} from "@/lib/serverComm";
import {
  useCurrentAccountId,
  useCurrentWorkspace,
} from "@/store/authContext/authContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Reports from "@/pages/Reports";
import KPI from "@/components/system-workspaces/KPI";
import DirectionalDrivers from "@/components/system-workspaces/DirectionalDrivers";
import { ReportForUIV2 as ReportType } from "@/types/report";
import { ReportConfigV2 } from "@/common/report-configuration";
import { DatasetName } from "@/common/constants";
import ReportV2 from "@/components/report/ReportV2";
import { Teaser } from "@/components/charts/components/teaser";
import ReportDesignerV2 from "./ReportDesignerV2";
import type {
  SystemWorkspaceKPIValue,
  SystemWorkspaceResponse,
  SystemWorkspaceSpecialComponentData,
} from "@/common/system-workspace";
import type { Driver } from "@/components/system-workspaces/DirectionalDrivers";
import mixpanel from "@/lib/mixpanel";
import {
  initialReportDesignerState,
  reducer as reportDesignerStateReducer,
  type ReportDesignerState,
  type ReportStateReducerActionType,
} from "@/common/reportDesignerStateReducer";

const paritaLogoCopilot = new URL(
  "../assets/peoplescape-assistant-1.png",
  import.meta.url,
).toString();

/**
 * Wrapper component that provides local reducer state for a report.
 * This enables filter editing within the system workspace without persisting changes.
 */
const ReportWithLocalState: React.FC<{
  report: ReportType;
  onEdit: () => void;
  onRemove: () => void;
  onError: () => void;
}> = ({ report, onEdit, onRemove, onError }) => {
  const workspace = useCurrentWorkspace();
  const reducerWithOverrides = useCallback(
    (state: ReportDesignerState, action: ReportStateReducerActionType) =>
      reportDesignerStateReducer(
        state,
        action,
        workspace.dataset_field_configs,
      ),
    [workspace.dataset_field_configs],
  );
  const [state, dispatch] = useReducer(reducerWithOverrides, {
    ...initialReportDesignerState(report.config.data.datasetName),
    reportConfig: report.config,
    reportId: report.id,
  });

  // Sync local state when the report config changes externally
  useEffect(() => {
    dispatch({ type: "setReportConfig", payload: report.config });
  }, [report.config]);

  return (
    <ReportV2
      id={report.id}
      reportConfig={state.reportConfig}
      reportDesignerDispatch={dispatch}
      newFilterField={state.newFilterDefinitionName}
      onEdit={onEdit}
      size="md"
      onRemoveFromPage={onRemove}
      isSystemReport={!!report.system_report_name}
      onError={onError}
      disableSummaryRegeneration={true}
    />
  );
};

interface SystemWorkspacePageProps {
  workspaceId: string;
  requiredDatasets?: DatasetName[];
}

/**
 * Wrapper component that checks for required datasets
 */
export const SystemWorkspaceWrapper: React.FC<SystemWorkspacePageProps> = ({
  workspaceId,
  requiredDatasets = [],
}) => {
  const paritaWorkspace = useCurrentWorkspace();

  // Check if all required datasets are available
  const hasRequiredDatasets =
    requiredDatasets.length === 0 ||
    requiredDatasets.every((ds) =>
      paritaWorkspace?.dataset_access.includes(ds),
    );

  if (!hasRequiredDatasets) {
    return (
      <div className="flex w-full h-screen justify-center items-center">
        <Teaser />
      </div>
    );
  }

  // Key forces remount when workspaceId changes, ensuring fresh state
  return <SystemWorkspacePage key={workspaceId} workspaceId={workspaceId} />;
};

/**
 * Main system workspace page component
 */
const SystemWorkspacePage: React.FC<{ workspaceId: string }> = ({
  workspaceId,
}) => {
  const currentAccountId = useCurrentAccountId();
  const currentWorkspace = useCurrentWorkspace();
  const navigate = useNavigate();

  // State
  const [metrics, setMetrics] = useState<
    Record<string, SystemWorkspaceKPIValue>
  >({});
  const [reports, setReports] = useState<ReportType[]>([]);
  const [specialComponents, setSpecialComponents] = useState<
    SystemWorkspaceSpecialComponentData[]
  >([]);
  const [localReportConfigs, setLocalReportConfigs] = useState<
    Record<string, ReportConfigV2>
  >({});

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(true);

  // Modal states
  const [isCreateReportModalOpen, setIsCreateReportModalOpen] = useState(false);
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);

  // Refs
  const erroredReportsRef = useRef<Set<string>>(new Set());
  const prevCreateReportModalRef = useRef(false);

  // Clear errored reports when account changes
  useEffect(() => {
    erroredReportsRef.current.clear();
    setSpecialComponents([]);
  }, [currentAccountId]);

  // Handle report modal closing and refresh reports
  useEffect(() => {
    if (prevCreateReportModalRef.current && !isCreateReportModalOpen) {
      const fetchReportsAndMetrics = async () => {
        try {
          const response: SystemWorkspaceResponse =
            await getSystemWorkspaceReports(workspaceId);
          setMetrics(response.kpis);
          setReports(response.reports as ReportType[]);
          setSpecialComponents(response.specialComponents || []);
        } catch (error) {
          console.error("Error fetching workspace reports:", error);
        }
      };
      fetchReportsAndMetrics();
    }
    prevCreateReportModalRef.current = isCreateReportModalOpen;
  }, [isCreateReportModalOpen, workspaceId]);

  // Report error handler
  const handleReportError = useCallback((reportId: string) => {
    erroredReportsRef.current.add(reportId);
    setReports((prev) => [...prev]);
  }, []);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      if (!currentAccountId) return;

      // Reset state for the new workspace
      setMetrics({});
      setReports([]);
      setSpecialComponents([]);
      setLocalReportConfigs({});
      erroredReportsRef.current.clear();

      setMetricsLoading(true);

      try {
        const workspaceResponse = await getSystemWorkspaceReports(workspaceId);
        setMetrics(workspaceResponse.kpis);
        setReports(workspaceResponse.reports as ReportType[]);
        setSpecialComponents(workspaceResponse.specialComponents || []);
      } catch (error) {
        console.error("Error fetching workspace data:", error);
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchData();
  }, [currentAccountId, workspaceId]);

  // Sync local report configs with reports
  useEffect(() => {
    const newConfigs = reports
      .filter((report) => report?.id)
      .reduce(
        (acc, report) => {
          if (
            !localReportConfigs[report.id] ||
            JSON.stringify(localReportConfigs[report.id]) !==
              JSON.stringify(report.config)
          ) {
            acc[report.id] = report.config;
          } else {
            acc[report.id] = localReportConfigs[report.id];
          }
          return acc;
        },
        {} as Record<string, ReportConfigV2>,
      );

    if (JSON.stringify(newConfigs) !== JSON.stringify(localReportConfigs)) {
      setLocalReportConfigs(newConfigs);
    }
  }, [reports, localReportConfigs]);

  // Handlers
  const handleSubmit = async (userQuery: string) => {
    setIsLoading(true);
    try {
      navigate(`/new-chat`, {
        replace: true,
        state: { initialQuery: userQuery, type: workspaceId },
      });
    } catch (error) {
      console.error("Error creating chat:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReportSave = async (reportId: string) => {
    try {
      const updatedReports = await addSystemWorkspaceReport(
        workspaceId,
        reportId,
      );
      setReports(updatedReports);

      const newConfigs = updatedReports.reduce(
        (acc: Record<string, ReportConfigV2>, report: ReportType) => {
          acc[report.id] = report.config;
          return acc;
        },
        {},
      );
      setLocalReportConfigs(newConfigs);
      setIsCreateReportModalOpen(false);
    } catch (error) {
      console.error("Error adding report to workspace:", error);
    }
  };

  const handleReportClick = async (reportId: string) => {
    setIsChartModalOpen(false);
    try {
      const updatedReports = await addSystemWorkspaceReport(
        workspaceId,
        reportId,
      );
      setReports(updatedReports);

      const newConfigs = updatedReports.reduce(
        (acc: Record<string, ReportConfigV2>, report: ReportType) => {
          acc[report.id] = report.config;
          return acc;
        },
        {},
      );
      setLocalReportConfigs(newConfigs);
    } catch (error) {
      console.error("Error adding report to workspace:", error);
    }
  };

  const handleRemoveReport = useCallback(
    async (reportId: string) => {
      try {
        const updatedReports = await removeSystemWorkspaceReport(
          workspaceId,
          reportId,
        );
        setReports(updatedReports);

        setLocalReportConfigs(() => {
          return updatedReports.reduce(
            (acc: Record<string, ReportConfigV2>, report: ReportType) => {
              acc[report.id] = report.config;
              return acc;
            },
            {},
          );
        });
      } catch (error) {
        console.error("Error removing report from workspace:", error);
      }
    },
    [workspaceId],
  );

  const renderReport = useCallback(
    (report: ReportType) => {
      if (!report?.id || erroredReportsRef.current.has(report?.id)) {
        return null;
      }

      return (
        <div
          key={report.id}
          className="w-full h-auto overflow-hidden [&:fullscreen]:h-screen [&:fullscreen]:overflow-y-auto"
          id={`report-${report.id}`}
        >
          <ReportWithLocalState
            report={{
              ...report,
              config: localReportConfigs[report.id] || report.config,
            }}
            onEdit={() => {
              try {
                // Use workspace-specific route prefix
                mixpanel.track("Edit Smart Workspace Report", {
                  eventDescription: report.title,
                  reportId: report.id,
                });
                navigate(`/areport/${report.id}`);
              } catch (error) {
                console.error("Error navigating to report:", error);
                window.location.href = `/areport/${report.id}`;
              }
            }}
            onRemove={() => handleRemoveReport(report.id)}
            onError={() => handleReportError(report.id)}
          />
        </div>
      );
    },
    [localReportConfigs, handleRemoveReport, handleReportError, navigate],
  );

  // Render special component based on type
  const renderSpecialComponent = useCallback(
    (component: SystemWorkspaceSpecialComponentData) => {
      if (component.type === "directional_drivers") {
        // Type guard for directional drivers data
        const data = component.data as
          | {
              drivers?: Driver[];
              methodologyNotes?: string;
            }
          | null
          | undefined;
        const driversData = Array.isArray(data?.drivers) ? data.drivers : [];
        return (
          <DirectionalDrivers
            key={`special-${component.type}-${component.position}`}
            data={{
              drivers: driversData,
              methodologyNotes: data?.methodologyNotes || "",
            }}
            loading={component.loading || false}
          />
        );
      }
      // Add more component types here as needed
      return null;
    },
    [],
  );

  // Merge reports and special components into a unified array sorted by position
  const renderItems = useCallback(() => {
    type RenderItem =
      | { type: "report"; position: number; data: ReportType }
      | {
          type: "special";
          position: number;
          data: SystemWorkspaceSpecialComponentData;
        };

    const items: RenderItem[] = [
      ...reports.map((report, index) => ({
        type: "report" as const,
        position: index,
        data: report,
      })),
      ...specialComponents.map((component) => ({
        type: "special" as const,
        position: component.position,
        data: component,
      })),
    ];

    // Sort by position
    items.sort((a, b) => a.position - b.position);

    return items.map((item) => {
      if (item.type === "report") {
        return renderReport(item.data);
      } else {
        return renderSpecialComponent(item.data);
      }
    });
  }, [reports, specialComponents, renderReport, renderSpecialComponent]);

  return (
    <div className="flex w-full relative justify-center">
      <div className="border-r border-gray-200 bg-white p-6 w-full max-w-6xl dark:bg-gray-800 dark:border-gray-700">
        {/* Create Report Modal */}
        {isCreateReportModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="relative bg-white rounded-xl shadow-2xl w-[95vw] h-[95vh] max-w-8xl pt-2">
              <X
                className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 cursor-pointer z-10"
                size={24}
                onClick={() => setIsCreateReportModalOpen(false)}
              />
              <div className="w-full h-full overflow-hidden rounded-xl">
                <ReportDesignerV2
                  onReportSave={handleReportSave}
                  disableNavigationOnSave={true}
                />
              </div>
            </div>
          </div>
        )}

        <img
          src={paritaLogoCopilot}
          alt="Peoplescape Logo"
          className="w-[400px] mb-8 mx-auto h-auto"
        />

        <div className="flex">
          <QueryBar
            className="mb-8"
            outerClassName="flex-grow"
            isLoading={isLoading}
            supressDisclaimer={true}
            onSubmit={handleSubmit}
          />
          <button
            className="h-[50px] ml-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            onClick={() => setIsChartModalOpen(true)}
          >
            Add a Report
          </button>
          <button
            className="h-[50px] ml-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            onClick={() => setIsCreateReportModalOpen(true)}
          >
            Create a Report
          </button>
        </div>

        <div className="flex flex-col gap-6">
          <KPI
            metrics={metrics}
            metricsLoading={metricsLoading}
            currentWorkspace={currentWorkspace}
          />
          <div className="flex-1 space-y-4 min-w-[400px]">
            {metricsLoading ? (
              // Loading skeleton for reports
              [...Array(2)].map((_, index) => (
                <div
                  key={index}
                  className="w-full border rounded-lg p-4 dark:border-gray-700"
                >
                  <div className="animate-pulse">
                    <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded-lg mb-4"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                  </div>
                </div>
              ))
            ) : (
              <>
                {renderItems()}

                <div className="flex">
                  <button
                    onClick={() => setIsChartModalOpen(true)}
                    className="w-1/3 aspect-square border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex flex-col items-center justify-center p-4 hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
                  >
                    <Plus className="w-8 h-8 text-gray-400 dark:text-gray-500 mb-2" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Add your own report
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <Dialog open={isChartModalOpen} onOpenChange={setIsChartModalOpen}>
          <DialogContent className="sm:max-w-[80%] h-[80vh]">
            <DialogHeader>
              <DialogTitle>Select Report</DialogTitle>
              <DialogDescription>
                Choose a report to add to your dashboard
              </DialogDescription>
            </DialogHeader>
            <Reports onReportClick={handleReportClick} />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};
